import { computed, getCurrentInstance } from 'vue'
import { isIos } from '@/helpers/device'
import { useMainStore } from '@/store/main'
import { useI18nStore } from '@/store/i18n'
import { useLocationStore } from '@/store/location'
import { useSurveyStore } from '@/store/survey'
import { useUserFlagsStore } from '@/store/userFlags'
import { useCoreFeaturesStore } from '@/store/coreFeatures'
import { useSubscriptionStore } from '@/store/subscription'
import { usePaywallStore } from '@/store/paywall'
import { useFunnelType } from '@/composables/useFunnelType'

/**
 * Provides computed A/B experiment flags and feature flags.
 * Requires $absmartly to be available on the Vue instance (set up by Experiments mixin in App.vue).
 *
 * Usage in setup():
 *   const { IS_EMAIL_SUGGEST_EXP, IS_PRIMER_GOOGLE_PAY_EXP } = useABFeatureFlag()
 */
export function useABFeatureFlag() {
	const instance = getCurrentInstance()
	const proxy = instance?.proxy

	// Helper to access $absmartly treatment. Falls back to 0 if not yet initialized.
	// Reading isAbSmartlyReady.value creates a reactive dependency so that computed flags
	// re-evaluate once the ABSmartly context becomes available.
	const treatment = (experimentName) => {
		if (!isAbSmartlyReady.value) return 0
		return proxy?.$absmartly?.treatment(experimentName) ?? 0
	}

	const mainStore = useMainStore()
	const i18nStore = useI18nStore()
	const locationStore = useLocationStore()
	const surveyStore = useSurveyStore()
	const userFlagsStore = useUserFlagsStore()
	const coreFeaturesStore = useCoreFeaturesStore()
	const subsStore = useSubscriptionStore()
	const paywallStore = usePaywallStore()

	// Reactive flag that flips when $absmartly context becomes available.
	// Used as a dependency inside treatment() to force computed flags to recompute.
	const isAbSmartlyReady = computed(() => mainStore.isAbSmartlyReady)

	// Store getters as computed refs
	const language = computed(() => i18nStore.getLanguage)
	const accountPlatform = computed(() => locationStore.accountPlatform)
	// getQueryParam is a getter that returns a function: (param) => value
	// We expose it as a plain function so this.getQueryParam('foo') works in Options API.
	const getQueryParam = (param) => locationStore.getQueryParam(param)
	const userBmi = computed(() => mainStore.getBmi)
	const countryCode = computed(() => mainStore.getCountryCode)
	const regionCode = computed(() => mainStore.getRegionCode)
	const getPropertyValue = computed(() => surveyStore.getPropertyValue)
	const appTheme = computed(() => mainStore.getAppTheme)
	const isOrganicTrafficOnboarding = computed(() => userFlagsStore.getIsOrganicTrafficOnboarding)
	const FEAT_CONFIRM_EMAIL_BY_CODE = computed(() => coreFeaturesStore.FEAT_CONFIRM_EMAIL_BY_CODE)
	const subscriptionProvider = computed(() => subsStore.checkProvider)
	const subscriptionPeriodType = computed(() => subsStore.checkPeriodType)
	const canDowngradeSubscription = computed(() => subsStore.canDowngrade)
	const lastUsedProvider = computed(() => paywallStore.getLastUsedProvider)
	const subscription = computed(() => subsStore.getSubscription)
	const userGeo = computed(() => mainStore.getGeo)

	const { AREA } = useFunnelType()

	// --- Experiment flags ---

	const IS_OPTIMIZE_POST_PURCHASE_FLOW_EXP = computed(() => {
		return treatment('web_optimize_post_purchase_flow')
	})
	const IS_SIMPLE_WEB_APP_MVP_EXP = computed(() => {
		return treatment('web_simple_mvp_launch')
	})
	const IS_ACCOUNT_DELETION_DEEPLINK_EXP = computed(() => {
		return getQueryParam('exp')?.includes?.('ios_improve_account_deletion')
	})
	const IS_ACCOUNT_DELETION_DEEPLINK_V2_EXP = computed(() => {
		return getQueryParam('exp')?.includes?.('auth0_rollout_deletion_v2')
	})
	const IS_ANDROID_ACCOUNT_DELETION_DEEPLINK_EXP = computed(() => {
		return getQueryParam('exp')?.includes?.('android_improve_account_deletion')
	})
	const IS_AUTH0_ACCOUNT_DELETION_DEEPLINK_ACCOUNT_DELETION = computed(() => {
		return getQueryParam('exp')?.includes?.('auth0_rollout_deletion')
	})
	const IS_CONFIRM_EMAIL_BY_CODE_EXP = computed(() => {
		return (accountPlatform.value.ios || accountPlatform.value.android) && FEAT_CONFIRM_EMAIL_BY_CODE.value
	})
	const IS_CLICK_TO_CANCEL_CALIFORNIA = computed(() => {
		return regionCode.value?.toLowerCase?.() === 'ca'
	})
	const IS_CANCELLATION_NEGATIVITY_REDUCTION = computed(() => {
		return accountPlatform.value.android && treatment('web_cancellation_negativity_reduction')
	})
	const IS_DLOCAL_ACQUIRING_IN_BR_AND_MX_EXP = computed(() => {
		return ['mx'].includes(countryCode.value) && treatment('web_dlocal_acquiring_in_mx')
	})
	const IS_FEMALE_WALKING_OPTIMIZATION_EXP = computed(() => {
		return AREA.value.WALKING_FEMALE && language.value === 'en'
	})
	const IS_ONE_PREDICT_INSTEAD_OF_MANY_EXP = computed(() => {
		return treatment('web_one_predict_instead_of_many')
	})
	const IS_OFFERING_TRIAL_WITH_COMMITMENTS_I3_MALE_EXP = computed(() => {
		return AREA.value.BAU_MALE && treatment('web_offering_trial_with_commitments_i3_male')
	})
	const IS_WHEEL_OF_FORTUNE_MALE_EXP = computed(() => {
		return AREA.value.BAU_MALE && treatment('web_wheel_of_fortune_male')
	})
	const IS_WHEEL_OF_FORTUNE_FEMALE_ACTIVITY_EXP = computed(() => {
		return AREA.value.ACTIVITY_FEMALE && treatment('web_wheel_of_fortune_activity_female')
	})
	const IS_WHEEL_OF_FORTUNE_WALKING_FEMALE_EXP = computed(() => {
		return AREA.value.WALKING_FEMALE && treatment('web_wheel_of_fortune_walking_female')
	})
	const IS_WHEEL_OF_FORTUNE_YOGA_FEMALE_EXP = computed(() => {
		return AREA.value.YOGA && treatment('web_wheel_of_fortune_yoga_female')
	})
	const IS_WEB_SUMMARY_CHECKPOINTS_WALKING = computed(() => {
		return treatment('web_summary_checkpoints_walking')
	})
	const IS_WEB_SUMMARY_CHECKPOINTS_YOGA = computed(() => {
		return treatment('web_summary_checkpoints_yoga')
	})
	const IS_WEB_SUMMARY_CHECKPOINTS_TAI_CHI_MALE = computed(() => {
		return treatment('web_summary_checkpoints_tai_chi_male')
	})
	const IS_DEEP_LINKS_TAI_CHI_EXP = computed(() => {
		return isIos && treatment('web_deep_links_tai_chi')
	})
	const IS_WHEEL_OF_FORTUNE_FEMALE_FASTING_EXP = computed(() => {
		// iteration 3
		return treatment('web_wheel_of_fortune_female')
	})
	const IS_WARMUP_BEFORE_WINBACK_OFFER_EXP = computed(() => {
		if (subscriptionPeriodType.value.isCommitment) {
			return false
		}
		return treatment('web_warmup_before_winback_offer')
	})
	const IS_QUESTION_FOR_CEO_EXP = computed(() => {
		return countryCode.value === 'us'
	})
	const IS_NEW_APP_DOWNLOAD_SCREEN_EXP = computed(() => {
		return treatment('web_new_app_download_screen')
	})
	const IS_PARTIAL_RETRIES_FOR_UPSALES_EXP = computed(() => {
		return subscriptionProvider.value.isPalta && treatment('web_partial_retries_for_upsales')
	})
	const IS_COMMITMENT_CANCELLATION_DYNAMIC_EXP_B = computed(() => {
		return subscriptionPeriodType.value.isCommitment && treatment('web_commitment_cancellation_dynamic') === 1
	})
	const IS_COMMITMENT_CANCELLATION_DYNAMIC_EXP_C = computed(() => {
		return subscriptionPeriodType.value.isCommitment && treatment('web_commitment_cancellation_dynamic') === 2
	})
	const IS_TRANSACTION_DELAY_FOR_ONE_CLICK_EXP = computed(() => {
		return treatment('web_transaction_delay_for_one_click')
	})
	const IS_EMAIL_DELAY_3MIN_EXP = computed(() => {
		return treatment('web_email_delay_3min')
	})
	const IS_PHONE_NUMBERS_ON_THE_CONFIRMATION_SCREEN_EXP = computed(() => {
		return treatment('web_phone_numbers_on_the_confirmation_screen')
	})
	const IS_WALKING_GS_SCROLLING_URGENCY_MESSAGE_EXP = computed(() => {
		return treatment('web_walking_gs_scrolling_urgency_message')
	})
	const IS_BUY_6_GET_12_PILATES_EXP = computed(() => {
		return AREA.value.PILATES_FEMALE && treatment('web_buy_6_get_12_pilates')
	})
	const IS_PRICING_ON_FASTING_FUNNELS_WITH_SP_EXP = computed(() => {
		return treatment('web_pricing_on_fasting_funnels_with_sp_nonus')
	})
	const IS_PRICING_ON_PILATES_FUNNEL_WITH_SP_EXP = computed(() => {
		return treatment('web_pricing_on_pilates_funnel_with_sp')
	})
	const IS_PILATES_FEMALE_PROGRESS_TEASER_EXP = computed(() => {
		return treatment('web_pilates_female_progress_teaser')
	})
	const IS_WALKING_FEMALE_PROGRESS_TEASER_EXP = computed(() => {
		return treatment('web_walking_female_progress_teaser')
	})
	const IS_SCONE_ADAPTED_COPY_GB_EXP_B = computed(() => {
		return treatment('web_scone_adapted_copy_gb') === 1
	})
	const IS_SCONE_ADAPTED_COPY_GB_EXP_C = computed(() => {
		return treatment('web_scone_adapted_copy_gb') === 2
	})
	const IS_BULK_CHANGES_WALKING_FEMALE_EXP = computed(() => {
		return AREA.value.WALKING_FEMALE && countryCode.value === 'us'
	})
	const IS_APPLE_PAY_CIT_E_COMMERCE_AUTH_EXP = computed(() => {
		return treatment('web_apple_pay_cit_e_commerce_auth')
	})
	const IS_PRIMER_GOOGLE_PAY_EXP = computed(() => {
		return treatment('web_primer_google_pay')
	})
	const IS_SHORT_PLAN_DOWNGRADE_EXP = computed(() => {
		return canDowngradeSubscription.value && treatment('web_short_plan_downgrade')
	})
	const IS_PILETS_PLAN_PREVIEW_BEFORE_PAYWALL_EXP = computed(() => {
		return treatment('web_pilates_plan_preview_before_paywall')
	})
	const IS_EMAIL_SUGGEST_EXP = computed(() => {
		return treatment('web_email_suggest')
	})
	const IS_FASTING_F_START_DATE_ON_WELCOME_EXP_B = computed(() => {
		return treatment('web_fasting_fem_start_date_and_people_on_welcome') === 1
	})
	const IS_FASTING_F_START_DATE_ON_WELCOME_EXP_C = computed(() => {
		return treatment('web_fasting_fem_start_date_and_people_on_welcome') === 2
	})
	const IS_LOGIN_MENU_EMAIL_CAPTURE_EXP = computed(() => {
		return treatment('web_login_menu_email_capture')
	})
	const IS_PIX_PAYMENT_METHOD_FOR_BR_EXP = computed(() => {
		if (subscription.value?.payment_method === 'pix_automatico') {
			return true
		}
		return treatment('web_pix_payment_method_for_br')
	})
	const IS_DATA_INPUT_SCREENS_OPTIMIZED_EXP = computed(() => {
		return treatment('web_data_input_screens_optimized')
	})
	const IS_FASTING_F_LOADER_BEFORE_EMAIL_EXP = computed(() => {
		return treatment('web_fasting_fem_loader_before_email')
	})
	const IS_SALES_TAX_EXCLUSION_CHAIR_WORKOUTS_EXP = computed(() => {
		const hasUserGeo = Boolean(userGeo.value?.zip && userGeo.value?.city && userGeo.value?.region)
		return hasUserGeo && treatment('web_chair_workouts_male_sales_tax_exclusion_i2')
	})
	const IS_FASTING_F_SOCIAL_PROOF_ON_FIRST_AND_PAYWALL_EXP = computed(() => {
		return treatment('web_fasting_fem_social_proof_on_first_and_paywall')
	})
	const IS_CHAIR_WORKOUTS_MALE_LOADER_TEASER_EXP = computed(() => {
		return treatment('web_chair_workouts_male_loader_teaser')
	})
	const IS_FIRST_AND_LAST_NAME_IN_THE_PAYMENT_FORM_EXP = computed(() => {
		return Boolean(treatment('web_first_and_last_name_in_the_payment_form'))
	})
	const IS_DOWNLOAD_INVITE_CONFIRMATION_SCREEN_EXP_B = computed(() => {
		return treatment('web_download_invite_confirmation_screen') === 1
	})
	const IS_DOWNLOAD_INVITE_CONFIRMATION_SCREEN_EXP_C = computed(() => {
		return treatment('web_download_invite_confirmation_screen') === 2
	})
	const IS_AI_SLIM_FACE_FEMALE_B_EXP = computed(() => {
		// /result_complete?safe=true&host=simple.life&country=US&lang=en&utm_source=dashboard&exp=web_ai_slim_face_female~1&area=female&mock-data=true
		return treatment('web_ai_slim_face_female') === 1
	})
	const IS_AI_SLIM_FACE_FEMALE_C_EXP = computed(() => {
		// /result_complete?safe=true&host=simple.life&country=US&lang=en&utm_source=dashboard&exp=web_ai_slim_face_female~2&area=female&mock-data=true
		return treatment('web_ai_slim_face_female') === 2
	})
	const IS_TAI_CHI_FEM_V2_SOCIAL_PROOF_EXP = computed(() => {
		return AREA.value.WALKING_TAI_CHI_FEMALE_V2 && treatment('web_tai_chi_fem_v2_social_proof')
	})
	const IS_BLURRED_RESULT_EMAIL_CAPTURE_EXP = computed(() => {
		return treatment('web_blurred_result_email_capture')
	})
	const IS_FEMALE_PILATES_BULK_UPDATE_EXP = computed(() => {
		return treatment('web_female_pilates_bulk_update')
	})
	const IS_FSA_HSA_PAYMENT_METHOD_EXP = computed(() => {
		return treatment('web_fsa_hsa_payment_method')
	})
	const IS_GAMIFY_LEARNING_WITHIN_THE_FUNNEL_EXP = computed(() => {
		return treatment('web_gamify_learning_within_the_funnel')
	})
	const IS_TAI_CHI_FEMALE_V2_MOTO_BLOCK_EXP = computed(() => {
		return treatment('web_tai_chi_female_v2_moto_block')
	})
	const IS_FEMALE_TAI_CHI_WALKING_V2_FR_EXP = computed(() => {
		return treatment('web_female_tai_chi_walking_v2_fr')
	})
	const IS_FEMALE_TAI_CHI_WALKING_V2_DE_EXP = computed(() => {
		return treatment('web_female_tai_chi_walking_v2_de')
	})
	const IS_MODELS_FROM_CREOS_EXP = computed(() => {
		return treatment('web_models_from_creos')
	})
	const IS_WEB_GLP_UPSELL_FAKE_DOOR_BANNER_EXP = computed(() => {
		return treatment('web_glp_upsell_fakedoor')
	})
	const IS_FASTING_F_SALES_TAX_EXCLUSION_EXP = computed(() => {
		const hasUserGeo = Boolean(userGeo.value?.zip && userGeo.value?.city && userGeo.value?.region)
		return hasUserGeo && treatment('web_fasting_fem_sales_tax_exclusion_i2')
	})

	return {
		language,
		accountPlatform,
		getQueryParam,
		userBmi,
		countryCode,
		regionCode,
		getPropertyValue,
		appTheme,
		isOrganicTrafficOnboarding,
		FEAT_CONFIRM_EMAIL_BY_CODE,
		subscriptionProvider,
		subscriptionPeriodType,
		canDowngradeSubscription,
		lastUsedProvider,
		subscription,
		userGeo,

		// Experiment flags
		IS_AI_SLIM_FACE_FEMALE_B_EXP,
		IS_AI_SLIM_FACE_FEMALE_C_EXP,
		IS_OPTIMIZE_POST_PURCHASE_FLOW_EXP,
		IS_SIMPLE_WEB_APP_MVP_EXP,
		IS_ACCOUNT_DELETION_DEEPLINK_EXP,
		IS_ACCOUNT_DELETION_DEEPLINK_V2_EXP,
		IS_ANDROID_ACCOUNT_DELETION_DEEPLINK_EXP,
		IS_AUTH0_ACCOUNT_DELETION_DEEPLINK_ACCOUNT_DELETION,
		IS_CONFIRM_EMAIL_BY_CODE_EXP,
		IS_CLICK_TO_CANCEL_CALIFORNIA,
		IS_CANCELLATION_NEGATIVITY_REDUCTION,
		IS_DLOCAL_ACQUIRING_IN_BR_AND_MX_EXP,
		IS_FEMALE_WALKING_OPTIMIZATION_EXP,
		IS_ONE_PREDICT_INSTEAD_OF_MANY_EXP,
		IS_OFFERING_TRIAL_WITH_COMMITMENTS_I3_MALE_EXP,
		IS_WHEEL_OF_FORTUNE_MALE_EXP,
		IS_WHEEL_OF_FORTUNE_FEMALE_ACTIVITY_EXP,
		IS_WHEEL_OF_FORTUNE_WALKING_FEMALE_EXP,
		IS_WHEEL_OF_FORTUNE_YOGA_FEMALE_EXP,
		IS_WEB_SUMMARY_CHECKPOINTS_WALKING,
		IS_WEB_SUMMARY_CHECKPOINTS_YOGA,
		IS_WEB_SUMMARY_CHECKPOINTS_TAI_CHI_MALE,
		IS_DEEP_LINKS_TAI_CHI_EXP,
		IS_WHEEL_OF_FORTUNE_FEMALE_FASTING_EXP,
		IS_WARMUP_BEFORE_WINBACK_OFFER_EXP,
		IS_QUESTION_FOR_CEO_EXP,
		IS_NEW_APP_DOWNLOAD_SCREEN_EXP,
		IS_PARTIAL_RETRIES_FOR_UPSALES_EXP,
		IS_COMMITMENT_CANCELLATION_DYNAMIC_EXP_B,
		IS_COMMITMENT_CANCELLATION_DYNAMIC_EXP_C,
		IS_TRANSACTION_DELAY_FOR_ONE_CLICK_EXP,
		IS_EMAIL_DELAY_3MIN_EXP,
		IS_PHONE_NUMBERS_ON_THE_CONFIRMATION_SCREEN_EXP,
		IS_WALKING_GS_SCROLLING_URGENCY_MESSAGE_EXP,
		IS_BUY_6_GET_12_PILATES_EXP,
		IS_PRICING_ON_FASTING_FUNNELS_WITH_SP_EXP,
		IS_PRICING_ON_PILATES_FUNNEL_WITH_SP_EXP,
		IS_PILATES_FEMALE_PROGRESS_TEASER_EXP,
		IS_WALKING_FEMALE_PROGRESS_TEASER_EXP,
		IS_SCONE_ADAPTED_COPY_GB_EXP_B,
		IS_SCONE_ADAPTED_COPY_GB_EXP_C,
		IS_BULK_CHANGES_WALKING_FEMALE_EXP,
		IS_APPLE_PAY_CIT_E_COMMERCE_AUTH_EXP,
		IS_PRIMER_GOOGLE_PAY_EXP,
		IS_SHORT_PLAN_DOWNGRADE_EXP,
		IS_PILETS_PLAN_PREVIEW_BEFORE_PAYWALL_EXP,
		IS_EMAIL_SUGGEST_EXP,
		IS_FASTING_F_START_DATE_ON_WELCOME_EXP_B,
		IS_FASTING_F_START_DATE_ON_WELCOME_EXP_C,
		IS_LOGIN_MENU_EMAIL_CAPTURE_EXP,
		IS_PIX_PAYMENT_METHOD_FOR_BR_EXP,
		IS_DATA_INPUT_SCREENS_OPTIMIZED_EXP,
		IS_FASTING_F_LOADER_BEFORE_EMAIL_EXP,
		IS_SALES_TAX_EXCLUSION_CHAIR_WORKOUTS_EXP,
		IS_FASTING_F_SOCIAL_PROOF_ON_FIRST_AND_PAYWALL_EXP,
		IS_CHAIR_WORKOUTS_MALE_LOADER_TEASER_EXP,
		IS_FIRST_AND_LAST_NAME_IN_THE_PAYMENT_FORM_EXP,
		IS_DOWNLOAD_INVITE_CONFIRMATION_SCREEN_EXP_B,
		IS_DOWNLOAD_INVITE_CONFIRMATION_SCREEN_EXP_C,
		IS_TAI_CHI_FEM_V2_SOCIAL_PROOF_EXP,
		IS_BLURRED_RESULT_EMAIL_CAPTURE_EXP,
		IS_FEMALE_PILATES_BULK_UPDATE_EXP,
		IS_FSA_HSA_PAYMENT_METHOD_EXP,
		IS_GAMIFY_LEARNING_WITHIN_THE_FUNNEL_EXP,
		IS_TAI_CHI_FEMALE_V2_MOTO_BLOCK_EXP,
		IS_FEMALE_TAI_CHI_WALKING_V2_FR_EXP,
		IS_FEMALE_TAI_CHI_WALKING_V2_DE_EXP,
		IS_MODELS_FROM_CREOS_EXP,
		IS_WEB_GLP_UPSELL_FAKE_DOOR_BANNER_EXP,
		IS_FASTING_F_SALES_TAX_EXCLUSION_EXP,
	}
}
